import { ApiError, unwrap, type Schemas } from '@mrigl/api-client';
import type { MessageKey } from '@mrigl/i18n';
import { Alert, Button, Card, CheckboxGroup, TextField } from '@mrigl/ui';
import { useCallback, useEffect, useState, type SubmitEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../api.js';
import { Loading } from '../../components.js';
import { useI18n, type I18n } from '../../i18n.js';
import { display, useStore } from '../catalog/shared.js';

type App = Schemas['ApiApp'];
type Scope = App['scopes'][number];
type Endpoint = Schemas['WebhookEndpoint'];
type EventName = Endpoint['events'][number];
type Delivery = Schemas['WebhookDelivery'];

const SCOPES: readonly Scope[] = [
  'products.read',
  'inventory.read',
  'inventory.write',
  'orders.read',
  'orders.write',
];
const EVENTS: readonly EventName[] = [
  'order.placed',
  'order.status_changed',
  'order.payment_changed',
  'product.created',
  'product.updated',
  'product.deleted',
  'inventory.availability_changed',
  'invoice.issued',
];

function integrationError(t: I18n['t'], failure: unknown): string {
  if (failure instanceof ApiError) {
    const urlProblem = failure.problem.errors?.find((item) => item.path === 'url')?.message;
    if (urlProblem !== undefined && /^url-(scheme|credentials|address|invalid)$/.test(urlProblem)) {
      return t(`admin.integrations.error.${urlProblem}` as MessageKey);
    }
    if (failure.code === 'webhooks-unavailable') return t('admin.integrations.webhooksDisabled');
    if (failure.code === 'conflict') return t('admin.integrations.error.limit');
    if (failure.code === 'version-conflict') return t('admin.integrations.error.version-conflict');
  }
  return errorMessage(t, failure);
}

/** Private apps (API tokens) and webhook endpoints of the store. */
export function IntegrationsPage() {
  const { storeId = '' } = useParams();
  const { t, locale } = useI18n();
  const { store, error } = useStore(storeId, t);
  if (error !== null) return <Alert tone="error">{error}</Alert>;
  if (store === null) return <Loading />;
  return (
    <div className="mrigl-stack">
      <p>
        <Link to={`/stores/${storeId}`}>{display(store.name, locale, store, store.slug)}</Link>
      </p>
      <h1 className="mrigl-heading">{t('admin.integrations.title')}</h1>
      <p>{t('admin.integrations.intro')}</p>
      <AppsSection storeId={storeId} />
      <WebhooksSection storeId={storeId} />
    </div>
  );
}

function AppsSection({ storeId }: { readonly storeId: string }) {
  const { t, formatDateTime } = useI18n();
  const [apps, setApps] = useState<App[] | null>(null);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>([]);
  const [token, setToken] = useState<{ name: string; value: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const list = await unwrap(
      api.GET('/admin-api/v1/stores/{storeId}/apps', { params: { path: { storeId } } }),
    );
    setApps(list.items);
  }, [storeId]);

  useEffect(() => {
    load().catch((failure: unknown) => {
      setError(errorMessage(t, failure));
      setApps([]);
    });
  }, [load, t]);

  async function create(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    if (name.trim() === '' || scopes.length === 0) {
      setError(t('admin.integrations.error.app'));
      return;
    }
    setBusy(true);
    try {
      const created = await unwrap(
        api.POST('/admin-api/v1/stores/{storeId}/apps', {
          params: { path: { storeId } },
          body: { name: name.trim(), scopes: scopes as Scope[] },
        }),
      );
      setToken({ name: created.app.name, value: created.token });
      setCopied(false);
      setName('');
      setScopes([]);
      await load();
    } catch (failure) {
      setError(integrationError(t, failure));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(app: App) {
    setError(null);
    try {
      await unwrap(
        api.POST('/admin-api/v1/stores/{storeId}/apps/{appId}/revoke', {
          params: { path: { storeId, appId: app.appId } },
        }),
      );
      setNotice(t('admin.integrations.revoked'));
      await load();
    } catch (failure) {
      setError(integrationError(t, failure));
    }
  }

  return (
    <Card labelledBy="apps-title">
      <h2 id="apps-title" className="mrigl-subheading">
        {t('admin.integrations.apps')}
      </h2>
      <p className="mrigl-muted">{t('admin.integrations.appsIntro')}</p>
      <div role="status">{notice === null ? null : <Alert tone="success">{notice}</Alert>}</div>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {token === null ? null : (
        <div
          className="admin-secret"
          role="region"
          aria-label={t('admin.integrations.tokenTitle', { name: token.name })}
        >
          <p>
            <strong>{t('admin.integrations.tokenTitle', { name: token.name })}</strong>
          </p>
          <p>{t('admin.integrations.tokenWarning')}</p>
          <code className="mrigl-ltr admin-secret__value">{token.value}</code>
          <Button
            variant="secondary"
            onClick={() => {
              void navigator.clipboard.writeText(token.value).then(() => {
                setCopied(true);
              });
            }}
          >
            {copied ? t('admin.copied') : t('admin.copy')}
          </Button>
        </div>
      )}
      {apps === null ? (
        <Loading />
      ) : apps.length === 0 ? (
        <p className="mrigl-muted">{t('admin.integrations.noApps')}</p>
      ) : (
        <div className="admin-table-wrap">
          <table className="mrigl-table">
            <thead>
              <tr>
                <th scope="col">{t('admin.integrations.column.name')}</th>
                <th scope="col">{t('admin.integrations.column.scopes')}</th>
                <th scope="col">{t('admin.integrations.column.token')}</th>
                <th scope="col">{t('admin.integrations.column.lastUsed')}</th>
                <th scope="col">{t('admin.integrations.column.status')}</th>
                <th scope="col">
                  <span className="mrigl-visually-hidden">{t('admin.order.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {apps.map((app) => (
                <tr key={app.appId}>
                  <td dir="auto">{app.name}</td>
                  <td>
                    {app.scopes.map((scope) => t(`admin.integrations.scope.${scope}`)).join(', ')}
                  </td>
                  <td className="mrigl-ltr">{`${app.tokenPrefix}…`}</td>
                  <td>
                    {app.lastUsedAt === null
                      ? t('admin.integrations.never')
                      : formatDateTime(app.lastUsedAt)}
                  </td>
                  <td>{t(`admin.integrations.appStatus.${app.status}`)}</td>
                  <td>
                    {app.status === 'active' ? (
                      <Button variant="secondary" onClick={() => void revoke(app)}>
                        {t('admin.integrations.revoke', { name: app.name })}
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <form
        className="mrigl-stack"
        aria-label={t('admin.integrations.createApp')}
        onSubmit={(event) => void create(event)}
      >
        <TextField
          label={t('admin.integrations.appName')}
          maxLength={80}
          dir="auto"
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <CheckboxGroup
          legend={t('admin.integrations.scopes')}
          options={SCOPES.map((scope) => ({
            value: scope,
            label: t(`admin.integrations.scope.${scope}`),
          }))}
          values={scopes}
          onChange={setScopes}
        />
        <Button type="submit" busy={busy}>
          {t('admin.integrations.createApp')}
        </Button>
      </form>
    </Card>
  );
}

function WebhooksSection({ storeId }: { readonly storeId: string }) {
  const { t } = useI18n();
  const [endpoints, setEndpoints] = useState<Endpoint[] | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const list = await unwrap(
      api.GET('/admin-api/v1/stores/{storeId}/webhooks', { params: { path: { storeId } } }),
    );
    setEndpoints(list.items);
    setEnabled(list.enabled);
  }, [storeId]);

  useEffect(() => {
    load().catch((failure: unknown) => {
      setError(errorMessage(t, failure));
      setEndpoints([]);
    });
  }, [load, t]);

  async function create(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (url.trim() === '' || events.length === 0) {
      setError(t('admin.integrations.error.endpoint'));
      return;
    }
    setBusy(true);
    try {
      await unwrap(
        api.POST('/admin-api/v1/stores/{storeId}/webhooks', {
          params: { path: { storeId } },
          body: { url: url.trim(), events: events as EventName[] },
        }),
      );
      setUrl('');
      setEvents([]);
      await load();
    } catch (failure) {
      setError(integrationError(t, failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card labelledBy="webhooks-title">
      <h2 id="webhooks-title" className="mrigl-subheading">
        {t('admin.integrations.webhooks')}
      </h2>
      <p className="mrigl-muted">{t('admin.integrations.webhooksIntro')}</p>
      {enabled ? null : <Alert tone="error">{t('admin.integrations.webhooksDisabled')}</Alert>}
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {endpoints === null ? (
        <Loading />
      ) : endpoints.length === 0 ? (
        <p className="mrigl-muted">{t('admin.integrations.noEndpoints')}</p>
      ) : (
        <ul className="mrigl-stack admin-endpoints">
          {endpoints.map((endpoint) => (
            <EndpointItem
              key={`${endpoint.endpointId}-${String(endpoint.version)}`}
              storeId={storeId}
              endpoint={endpoint}
              onChange={load}
            />
          ))}
        </ul>
      )}
      {enabled ? (
        <form
          className="mrigl-stack"
          aria-label={t('admin.integrations.addEndpoint')}
          onSubmit={(event) => void create(event)}
        >
          <TextField
            type="url"
            label={t('admin.integrations.url')}
            dir="ltr"
            maxLength={500}
            value={url}
            onChange={(event) => {
              setUrl(event.target.value);
            }}
          />
          <CheckboxGroup
            legend={t('admin.integrations.events')}
            options={EVENTS.map((name) => ({
              value: name,
              label: t(`admin.integrations.event.${name}`),
            }))}
            values={events}
            onChange={setEvents}
          />
          <Button type="submit" busy={busy}>
            {t('admin.integrations.addEndpoint')}
          </Button>
        </form>
      ) : null}
    </Card>
  );
}

function EndpointItem({
  storeId,
  endpoint,
  onChange,
}: {
  readonly storeId: string;
  readonly endpoint: Endpoint;
  readonly onChange: () => Promise<void>;
}) {
  const { t, formatDateTime } = useI18n();
  const [secret, setSecret] = useState<string | null>(null);
  const [deliveries, setDeliveries] = useState<Delivery[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const path = { storeId, endpointId: endpoint.endpointId };

  const run = async (work: () => Promise<void>, done?: string) => {
    setError(null);
    setNotice(null);
    try {
      await work();
      if (done !== undefined) setNotice(done);
    } catch (failure) {
      setError(integrationError(t, failure));
    }
  };

  const loadDeliveries = async () => {
    const list = await unwrap(
      api.GET('/admin-api/v1/stores/{storeId}/webhooks/{endpointId}/deliveries', {
        params: { path, query: { limit: 20 } },
      }),
    );
    setDeliveries(list.items);
  };

  const active = endpoint.status === 'active';
  return (
    <li className="mrigl-card mrigl-stack">
      <p className="mrigl-ltr">
        <strong>{endpoint.url}</strong>
      </p>
      <p>{endpoint.events.map((name) => t(`admin.integrations.event.${name}`)).join(', ')}</p>
      <p>
        <span
          className={`admin-status ${active ? 'admin-status--delivered' : 'admin-status--cancelled'}`}
        >
          {endpoint.disabledReason === 'failures'
            ? t('admin.integrations.disabledByFailures')
            : t(`admin.integrations.endpointStatus.${endpoint.status}`)}
        </span>
      </p>
      <div role="status">{notice === null ? null : <Alert tone="success">{notice}</Alert>}</div>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {secret === null ? null : (
        <p>
          {t('admin.integrations.secret')}:{' '}
          <code className="mrigl-ltr admin-secret__value">{secret}</code>
        </p>
      )}
      <div className="admin-actions">
        <Button
          variant="secondary"
          onClick={() =>
            void run(async () => {
              const result = await unwrap(
                api.GET('/admin-api/v1/stores/{storeId}/webhooks/{endpointId}/secret', {
                  params: { path },
                }),
              );
              setSecret(result.secret);
            })
          }
        >
          {t('admin.integrations.showSecret')}
        </Button>
        <Button
          variant="secondary"
          onClick={() =>
            void run(async () => {
              const result = await unwrap(
                api.POST('/admin-api/v1/stores/{storeId}/webhooks/{endpointId}/secret/rotate', {
                  params: { path },
                }),
              );
              setSecret(result.secret);
            }, t('admin.integrations.rotated'))
          }
        >
          {t('admin.integrations.rotateSecret')}
        </Button>
        {active ? (
          <Button
            variant="secondary"
            onClick={() =>
              void run(async () => {
                await unwrap(
                  api.POST('/admin-api/v1/stores/{storeId}/webhooks/{endpointId}/ping', {
                    params: { path },
                  }),
                );
              }, t('admin.integrations.pinged'))
            }
          >
            {t('admin.integrations.ping')}
          </Button>
        ) : null}
        <Button
          variant="secondary"
          onClick={() =>
            void run(async () => {
              await unwrap(
                api.PUT('/admin-api/v1/stores/{storeId}/webhooks/{endpointId}', {
                  params: { path },
                  body: {
                    url: endpoint.url,
                    events: endpoint.events,
                    active: !active,
                    version: endpoint.version,
                  },
                }),
              );
              await onChange();
            })
          }
        >
          {active ? t('admin.integrations.disable') : t('admin.integrations.enable')}
        </Button>
        <Button variant="secondary" onClick={() => void run(loadDeliveries)}>
          {t('admin.integrations.deliveries')}
        </Button>
        <Button
          variant="secondary"
          onClick={() =>
            void run(async () => {
              await unwrap(
                api.DELETE('/admin-api/v1/stores/{storeId}/webhooks/{endpointId}', {
                  params: { path },
                }),
              );
              await onChange();
            })
          }
        >
          {t('admin.integrations.delete')}
        </Button>
      </div>
      {deliveries === null ? null : deliveries.length === 0 ? (
        <p className="mrigl-muted">{t('admin.integrations.noDeliveries')}</p>
      ) : (
        <div className="admin-table-wrap">
          <table className="mrigl-table">
            <thead>
              <tr>
                <th scope="col">{t('admin.order.column.date')}</th>
                <th scope="col">{t('admin.integrations.events')}</th>
                <th scope="col">{t('admin.integrations.column.status')}</th>
                <th scope="col">{t('admin.integrations.column.result')}</th>
                <th scope="col">
                  <span className="mrigl-visually-hidden">{t('admin.order.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {deliveries.map((delivery) => (
                <tr key={delivery.deliveryId}>
                  <td>{formatDateTime(delivery.createdAt)}</td>
                  <td className="mrigl-ltr">{delivery.eventType}</td>
                  <td>
                    {t(`admin.integrations.delivery.${delivery.status}`)}
                    {' — '}
                    {t('admin.integrations.attempts', { count: delivery.attempts })}
                  </td>
                  <td className="mrigl-ltr">
                    {delivery.lastStatusCode === null
                      ? (delivery.lastError ?? '—')
                      : `HTTP ${String(delivery.lastStatusCode)}`}
                  </td>
                  <td>
                    {delivery.status === 'failed' ? (
                      <Button
                        variant="secondary"
                        onClick={() =>
                          void run(async () => {
                            await unwrap(
                              api.POST(
                                '/admin-api/v1/stores/{storeId}/webhooks/{endpointId}/deliveries/{deliveryId}/redeliver',
                                { params: { path: { ...path, deliveryId: delivery.deliveryId } } },
                              ),
                            );
                            await loadDeliveries();
                          }, t('admin.integrations.redelivered'))
                        }
                      >
                        {t('admin.integrations.redeliver')}
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </li>
  );
}
